import { useTranslation } from '@sortlist-frontend/translation/ssr';
import { assertNonEmptyString, HttpBadRequest } from '@sortlist-frontend/utils';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';

import { PageHead } from '_components/base/head';
import { Public404Page } from '_components/error/public-404-page';
import { PageSeoTag } from '_components/SEO';
import { GuideJsonLd } from '_components/SEO/GuideJsonLd';
import { AppProviders } from '_core/app-providers';
import { PublicAppContextProvider } from '_core/context/public-app-context';
import { isSerializedError } from '_core/error/error-serializer';
import { findGuidePageKey, GuidePage } from '_features/GuidePage';
import { getGuidePageServerSideProps, GuideRouteProps } from '_features/GuidePage/ssr/getGuidePageServerSideProps';
import { rankingConfig } from '_features/RankingPage/ranking.config';
import { RankingStaticLayout } from '_features/RankingPage/static/RankingStaticLayout';

type Props = GuideRouteProps & { pathname: string };

const Route = (props: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const { guideContent, guideData, locale, canonical, query, origin, error, pathname } = props;
  const { t } = useTranslation(rankingConfig.i18nNamespaces);

  if (guideContent == null || guideData == null || (error != null && isSerializedError(error))) {
    return <Public404Page message={t('common:pageErrors.occured')} locale={locale} />;
  }

  const navigationData = {
    origin,
    locale,
    resolvedUrl: pathname,
    query,
    languageSwitcthRedirectHome: true,
    domain: guideData.domain ?? undefined,
  };

  return (
    <PublicAppContextProvider canonical={canonical} origin={origin} locale={locale}>
      <PageHead canonical={canonical} query={query} origin={origin} locale={locale} />
      <PageSeoTag
        title={guideContent.seo.title}
        description={guideContent.seo.description}
        nextSeoProps={{
          openGraph: { type: 'article', title: guideContent.seo.ogTitle, description: guideContent.seo.ogDescription },
        }}
        canonical={canonical}
      />
      <GuideJsonLd content={guideContent} canonical={canonical} origin={origin} />
      <RankingStaticLayout
        navigationData={navigationData}
        homepageLinks={guideData.homepageLinks}
        expertises={guideData.expertises}>
        <GuidePage content={guideContent} />
      </RankingStaticLayout>
    </PublicAppContextProvider>
  );
};

export default function GuideRoute(props: InferGetServerSidePropsType<typeof getServerSideProps>) {
  return (
    <AppProviders dehydratedState={props.dehydratedState}>
      <Route {...props} />
    </AppProviders>
  );
}

export const getServerSideProps: GetServerSideProps<Props> = async (context) => {
  const { req } = context;
  const origin = req.headers['sortlist-origin'];
  const locale = req.headers['sortlist-locale'];
  const canonical = req.headers['sortlist-canonical'];
  const pathname = req.headers['sortlist-pathname'];

  assertNonEmptyString(origin, new HttpBadRequest({ message: 'Missing [origin] parameter' }));
  assertNonEmptyString(locale, new HttpBadRequest({ message: 'Missing [locale] parameter' }));
  assertNonEmptyString(canonical, new HttpBadRequest({ message: 'Missing [canonical] parameter' }));
  assertNonEmptyString(pathname, new HttpBadRequest({ message: 'Missing [pathname] parameter' }));

  const queryParams = context.resolvedUrl.split('?')[1] ?? '';
  const guidePageKey = findGuidePageKey({ origin, locale, pathname });

  if (guidePageKey == null || guidePageKey !== context.params?.key) {
    return { notFound: true };
  }

  const result = await getGuidePageServerSideProps(context, {
    guidePageKey,
    origin,
    locale,
    canonical,
    queryParams,
    pathname,
  });

  return 'props' in result ? { props: { ...(await result.props), pathname } } : result;
};
